import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { formatPhone } from "@/lib/phone";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CircleNotch, ArrowLeft, WarningCircle } from "@phosphor-icons/react";

async function fetchRefsInto(setSources, setUsers) {
  try {
    const [sources, users] = await Promise.all([
      api.get("/reference/lead-sources"),
      api.get("/users/options"),
    ]);
    setSources(sources.data);
    setUsers(users.data);
  } catch (e) {
    toast.error(formatApiError(e.response?.data?.detail));
  }
}

const today = () => new Date().toISOString().slice(0, 10);

function Section({ title, children }) {
  return (
    <div className="rounded-sm border border-[#E4E4E7] bg-white p-5">
      <h2 className="mb-4 text-xs font-bold uppercase tracking-[0.15em] text-[#032852]">{title}</h2>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function F({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">
        {label}{required ? " *" : ""}
      </Label>
      {children}
    </div>
  );
}

export default function NewProspect() {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [sources, setSources] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [duplicateMsg, setDuplicateMsg] = useState(null);

  const [org, setOrg] = useState({
    organization_name: "", website: "", linkedin_url: "",
    general_office_phone: "", general_email: "", notes: "",
  });
  const [contact, setContact] = useState({
    first_name: "", last_name: "", title: "", office_phone: "", mobile_phone: "", email: "",
  });
  const [lead, setLead] = useState({
    lead_source_id: "", lead_source_detail: "",
    relationship_stage: "cold", assigned_salesperson: "", date_entered: today(),
  });
  const [orig, setOrig] = useState({ type: "internal", user_id: "", name: "", organization: "", contact_info: "", notes: "" });

  const setO = (k) => (e) => setOrg((f) => ({ ...f, [k]: e.target.value }));
  const setC = (k) => (e) => setContact((f) => ({ ...f, [k]: e.target.value }));
  const setL = (k) => (e) => setLead((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const helpers = useRef({ fetchRefsInto });
  const loadRefs = useCallback(() => helpers.current.fetchRefsInto(setSources, setUsers), [helpers, setSources, setUsers]);
  useEffect(() => {
    loadRefs();
  }, [loadRefs]);

  const submit = async (e, confirmDuplicate = false) => {
    e?.preventDefault();
    if (!contact.office_phone && !contact.mobile_phone) {
      toast.error("The primary contact needs an office and/or mobile phone");
      return;
    }
    setLoading(true);
    try {
      const body = {
        organization_name: org.organization_name,
        website: org.website || null,
        linkedin_url: org.linkedin_url || null,
        general_office_phone: org.general_office_phone || null,
        general_email: org.general_email || null,
        notes: org.notes || null,
        primary_contact: {
          first_name: contact.first_name, last_name: contact.last_name, title: contact.title,
          office_phone: contact.office_phone || null, mobile_phone: contact.mobile_phone || null,
          email: contact.email,
        },
        lead_source_id: lead.lead_source_id,
        lead_source_detail: lead.lead_source_detail || null,
        relationship_stage: lead.relationship_stage || "cold",
        lead_originator: orig.type === "internal"
          ? { type: "internal", user_id: orig.user_id }
          : { type: "external", name: orig.name, organization: orig.organization || null, contact_info: orig.contact_info || null, notes: orig.notes || null },
        assigned_salesperson: isAdmin ? lead.assigned_salesperson : undefined,
        date_entered: lead.date_entered,
        confirm_duplicate: confirmDuplicate,
      };
      const { data } = await api.post("/prospects", body);
      toast.success("Prospect created");
      navigate(`/prospects/${data.id}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.duplicate) {
        setDuplicateMsg(detail.message);
      } else {
        toast.error(formatApiError(detail));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl p-6 sm:p-8" data-testid="new-prospect-page">
      <button onClick={() => navigate("/prospects")} className="mb-4 flex items-center gap-1.5 text-sm font-semibold text-[#032852] hover:text-[#569A1F]" data-testid="back-to-prospects">
        <ArrowLeft size={16} weight="bold" /> Prospects
      </button>
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#569A1F]">Sales</p>
      <h1 className="mt-2 font-heading text-3xl font-black tracking-tight text-[#032852] sm:text-4xl">New Prospect</h1>
      <p className="mb-6 mt-2 text-sm text-[#6D6D70]">Fast entry — properties and additional contacts can be added right after.</p>

      <form onSubmit={submit} className="space-y-5">
        <Section title="Organization">
          <F label="Company / organization name" required>
            <Input required value={org.organization_name} onChange={setO("organization_name")} className="rounded-sm" data-testid="org-name" />
          </F>
          <div className="grid gap-4 sm:grid-cols-2">
            <F label="Website"><Input value={org.website} onChange={setO("website")} className="rounded-sm" placeholder="https://..." data-testid="org-website" /></F>
            <F label="LinkedIn company page"><Input value={org.linkedin_url} onChange={setO("linkedin_url")} className="rounded-sm" placeholder="https://linkedin.com/company/..." /></F>
            <F label="General office phone"><Input value={org.general_office_phone} onChange={setO("general_office_phone")} onBlur={(e) => setO("general_office_phone")({ target: { value: formatPhone(e.target.value) } })} className="rounded-sm" /></F>
            <F label="General email"><Input type="email" value={org.general_email} onChange={setO("general_email")} className="rounded-sm" /></F>
          </div>
          <F label="Additional prospect notes"><Textarea value={org.notes} onChange={setO("notes")} rows={2} className="rounded-sm" /></F>
        </Section>

        <Section title="Primary Contact">
          <div className="grid gap-4 sm:grid-cols-2">
            <F label="First name" required><Input required value={contact.first_name} onChange={setC("first_name")} className="rounded-sm" data-testid="pc-first-name" /></F>
            <F label="Last name" required><Input required value={contact.last_name} onChange={setC("last_name")} className="rounded-sm" data-testid="pc-last-name" /></F>
            <F label="Job title / position" required><Input required value={contact.title} onChange={setC("title")} className="rounded-sm" data-testid="pc-title" /></F>
            <F label="Email" required><Input type="email" required value={contact.email} onChange={setC("email")} className="rounded-sm" data-testid="pc-email" /></F>
            <F label="Office phone"><Input value={contact.office_phone} onChange={setC("office_phone")} onBlur={(e) => setC("office_phone")({ target: { value: formatPhone(e.target.value) } })} className="rounded-sm" data-testid="pc-office-phone" /></F>
            <F label="Mobile phone"><Input value={contact.mobile_phone} onChange={setC("mobile_phone")} onBlur={(e) => setC("mobile_phone")({ target: { value: formatPhone(e.target.value) } })} className="rounded-sm" data-testid="pc-mobile-phone" /></F>
          </div>
          <p className="text-xs text-[#6D6D70]">At least one phone number (office or mobile) is required.</p>
        </Section>

        <Section title="Lead Information">
          <div className="grid gap-4 sm:grid-cols-2">
            <F label="Lead source" required>
              <Select value={lead.lead_source_id} onValueChange={setL("lead_source_id")} required>
                <SelectTrigger className="rounded-sm" data-testid="lead-source"><SelectValue placeholder="How did this lead originate?" /></SelectTrigger>
                <SelectContent>
                  {sources.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Lifecycle stage">
              <Select value={lead.relationship_stage} onValueChange={setL("relationship_stage")}>
                <SelectTrigger className="rounded-sm" data-testid="relationship-stage"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cold">Cold — identified potential customer; before meaningful back-and-forth communication</SelectItem>
                  <SelectItem value="warm">Warm — the prospect is communicating back and forth with TFS</SelectItem>
                  <SelectItem value="hot">Hot — a specific pursuit is in proposal or negotiation</SelectItem>
                </SelectContent>
              </Select>
            </F>
          </div>
          <F label="Lead source detail">
            <Input value={lead.lead_source_detail} onChange={setL("lead_source_detail")} className="rounded-sm" placeholder='e.g. "Referred by ABC Property Management" or "Met at BOMA event"' data-testid="lead-source-detail" />
          </F>
          <div className="grid gap-4 sm:grid-cols-2">
            {isAdmin ? (
              <F label="Assigned salesperson" required>
                <Select value={lead.assigned_salesperson} onValueChange={setL("assigned_salesperson")} required>
                  <SelectTrigger className="rounded-sm" data-testid="assigned-salesperson"><SelectValue placeholder="Select user" /></SelectTrigger>
                  <SelectContent>
                    {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </F>
            ) : (
              <F label="Assigned salesperson">
                <Input disabled value={user?.name || ""} className="rounded-sm bg-[#F4F4F5]" data-testid="assigned-salesperson-self" />
              </F>
            )}
            <F label="Date entered CRM" required>
              <Input type="date" required value={lead.date_entered} onChange={setL("date_entered")} className="rounded-sm" data-testid="date-entered" />
            </F>
          </div>
        </Section>

        <Section title="Lead Generated By / Originator">
          <div className="flex gap-2">
            <button type="button" onClick={() => setOrig((o) => ({ ...o, type: "internal" }))}
              className={`rounded-sm px-4 py-2 text-xs font-bold uppercase tracking-wide transition-colors ${orig.type === "internal" ? "bg-[#032852] text-white" : "bg-[#F4F4F5] text-[#52525B] hover:bg-[#E4E4E7]"}`}
              data-testid="originator-internal">
              TFS team member
            </button>
            <button type="button" onClick={() => setOrig((o) => ({ ...o, type: "external" }))}
              className={`rounded-sm px-4 py-2 text-xs font-bold uppercase tracking-wide transition-colors ${orig.type === "external" ? "bg-[#032852] text-white" : "bg-[#F4F4F5] text-[#52525B] hover:bg-[#E4E4E7]"}`}
              data-testid="originator-external">
              External person
            </button>
          </div>
          {orig.type === "internal" ? (
            <F label="TFS originator" required>
              <Select value={orig.user_id} onValueChange={(v) => setOrig((o) => ({ ...o, user_id: v }))} required>
                <SelectTrigger className="rounded-sm" data-testid="originator-user"><SelectValue placeholder="Who generated this lead?" /></SelectTrigger>
                <SelectContent>
                  {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              <F label="Originator name" required><Input required value={orig.name} onChange={(e) => setOrig((o) => ({ ...o, name: e.target.value }))} className="rounded-sm" data-testid="originator-name" /></F>
              <F label="Organization"><Input value={orig.organization} onChange={(e) => setOrig((o) => ({ ...o, organization: e.target.value }))} className="rounded-sm" /></F>
              <F label="Contact information"><Input value={orig.contact_info} onChange={(e) => setOrig((o) => ({ ...o, contact_info: e.target.value }))} className="rounded-sm" /></F>
              <F label="Notes"><Input value={orig.notes} onChange={(e) => setOrig((o) => ({ ...o, notes: e.target.value }))} className="rounded-sm" /></F>
            </div>
          )}
          <p className="text-xs text-[#6D6D70]">The Lead Originator is preserved permanently, even if ownership changes later.</p>
        </Section>

        {duplicateMsg && (
          <div className="flex items-start gap-3 rounded-sm border border-[#F59E0B]/40 bg-[#F59E0B]/10 p-4" data-testid="duplicate-warning">
            <WarningCircle size={20} weight="fill" className="mt-0.5 shrink-0 text-[#B45309]" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-[#78350F]">{duplicateMsg}</p>
              <div className="mt-3 flex gap-2">
                <Button type="button" size="sm" onClick={(e) => submit(e, true)} disabled={loading}
                        className="rounded-sm bg-[#B45309] text-xs font-bold text-white hover:bg-[#92400E]" data-testid="confirm-duplicate">
                  Create anyway
                </Button>
                <Button type="button" size="sm" variant="outline" className="rounded-sm text-xs" onClick={() => setDuplicateMsg(null)}>
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" className="rounded-sm" onClick={() => navigate("/prospects")}>Cancel</Button>
          <Button type="submit" disabled={loading} className="rounded-sm bg-[#569A1F] px-6 font-semibold text-white hover:bg-[#457d18]" data-testid="submit-prospect">
            {loading ? <CircleNotch size={18} className="animate-spin" /> : "Create prospect"}
          </Button>
        </div>
      </form>
    </div>
  );
}
